import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ApiService } from 'src/app/services/api.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';

// Constants
import { Urls } from 'src/app/lib/urls';
import { TRANSACTION_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { CASHIN_TABLE_SUB_HEADERS } from 'src/app/lib/constants/transactions.constants';
import { RbacService } from 'src/app/services/rbac.service'; 
import { Role } from 'src/app/lib/rbac.config';
@Component({
  selector: 'app-cash-in',
  templateUrl: './cash-in.component.html',
  styleUrls: ['./cash-in.component.scss'],
})
export class CashInComponent implements OnInit, OnDestroy {
  private readonly MAYA_PAYMENT_METHODS = ['maya online/qr', 'maya card'];
  private readonly PENDING_STATUS = 'pending';
  private readonly ALREADY_PROCESSED_MESSAGE = 'Transaction already processed';
  private readonly ALLOWED_REFRESH_ROLES: Role[] = [Role.SUPER_ADMIN, Role.ADMIN, Role.OPERATIONS];
  private readonly NOT_PERMITTED_MESSAGE = 'You are not allowed to refresh transactions. Please contact an admin if you need assistance.';
  
  apiService = inject(ApiService);
  showTable = true;
  private destroy$ = new Subject<void>();
  tabs = TRANSACTION_TAB_OPTIONS;
  public form: any;
  searchForm: any;
  limitForm: any;

  // Dropdown options
  paymentMethodOptions = [
    { label: 'Credit Card',               value: '1' },
    { label: 'Over the Counter',          value: '6' },
    { label: 'DragonPay or Deposit Slip', value: '3' },
    { label: 'DragonPay',                 value: '4' },
    { label: 'BPI Online Banking',        value: '2' },
    { label: 'EC Link',                   value: '5' },
    { label: 'Maya Online/QR',            value: '6' },
    { label: 'Maya',                      value: '7' },
  ];

  statusOptions = [
    { label: 'Success',  value: '1' },
    { label: 'Pending',  value: '0' },
    { label: 'Rejected', value: '3' },
    { label: 'Expired',  value: '4' },
    { label: 'Error',    value: '2' },
  ];

  paymentMethod: any = null;
  paymentMethodType_text: any = null;
  status: any = null;
  statusType_text: any = null;

  // Table
  tableSubHeader = CASHIN_TABLE_SUB_HEADERS;

  // Data
  itemLists: any[] | null = [];
  submitted = false;
  isLoading = false;
  fileName = '';

  // Filter fields — stores the submitted filter criteria
  filterFields: any = null;

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] | null = [];
  total = 0;

  // Search & Limit
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;

  // Sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Refresh State (Maya) ────────────────────────────────────────────────
  refreshingTransactionId: string | null = null;
  /** Transactions the backend confirmed are already processed — refresh permanently disabled */
  processedTransactionIds = new Set<string>();

  getRefreshButtonState(item: any): { disabled: boolean; tooltip: string } | null {
    const method = (item?.paymentMethod ?? '').toString().trim().toLowerCase();
    const status = (item?.status ?? '').toString().trim().toLowerCase();

    if (!this.MAYA_PAYMENT_METHODS.includes(method)) {
      return null;
    }

    if (!this.ALLOWED_REFRESH_ROLES.includes(this.rbac.role)) {
      return { disabled: true, tooltip: this.NOT_PERMITTED_MESSAGE };
    }

    if (this.processedTransactionIds.has(item.transactionId)) {
      return { disabled: true, tooltip: this.ALREADY_PROCESSED_MESSAGE };
    }
    if (status !== this.PENDING_STATUS) {
      return { disabled: true, tooltip: this.ALREADY_PROCESSED_MESSAGE };
    }
    if (this.refreshingTransactionId === item.transactionId) {
      return { disabled: true, tooltip: 'Refresh in progress…' };
    }
    return {
      disabled: false,
      tooltip: 'This refreshes and updates the transaction. This action cannot be undone',
    };
  }

  // ─── Resets ────────────────────────────────────────────────────────────────

  private resetForms(): void {
    this.form.reset({ search: '' });
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ search: '' });
  }

  private resetDropdowns(): void {
    this.paymentMethod = null;
    this.paymentMethodType_text = null;
    this.status = null;
    this.statusType_text = null;
  }

  private resetData(): void {
    this.itemLists = null;
    this.submitted = false;
    this.isLoading = false;
    this.fileName = '';
  }

  private resetFilterFields(): void {
    this.filterFields = null;
  }

  private resetPagination(): void {
    this.size = 10;
    this.total_pages = 0;
    this.current_page = 1;
    this.pages_array = null;
    this.total = 0;
  }

  private resetSearch(): void {
    this.search_keyword = '';
    this.searchKey = '';
    this.limitKey = '';
  }

  private resetLocalStorage(): void {
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  private resetTableState(): void {
    this.resetForms();
    this.resetDropdowns();
    this.resetData();
    this.resetFilterFields();
    this.resetPagination();
    this.resetSearch();
    this.resetLocalStorage();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  constructor(
    private fb: UntypedFormBuilder,
    private request: RequestsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService,
    private popUpDialog: PopUpDialogService,
    private rbac: RbacService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on init:', err);
    });

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.form.reset({ search: '' });
      this.resetTableState();
      this.showTable = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId: [null],
      firstName:     [null],
      middleName:    [null],
      lastName:      [null],
      phoneNumber:   [null],
      dateFrom:      [null],
      dateTo:        [null],
    });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.searchForm = this.fb.group({
      search: [''],
    });

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }, 500);

    this.limitForm = this.fb.group({
      limit: [this.size],
    });
  }

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  submit(): void {
    if (!this.validateDates()) {
      return;
    }

    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    this.current_page = 1;

    const params = {
      transactionId: this.form.controls.transactionId.value,
      firstName:     this.form.controls.firstName.value,
      middleName:    this.form.controls.middleName.value,
      lastName:      this.form.controls.lastName.value,
      phoneNumber:   this.form.controls.phoneNumber.value,
      dateFrom:      this.form.controls.dateFrom.value,
      dateTo:        this.form.controls.dateTo.value,
      paymentMethod: this.paymentMethod,
      status:        this.status,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    this.filterFields = params;
    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getCashInTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;
            this.submitted = true;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages,
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Cash In Transactions',
          );
        },
        complete: () => {
          this.form.reset();
          this.paymentMethod = null;
          this.paymentMethodType_text = null;
          this.status = null;
          this.statusType_text = null;

          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });

    this.showTable = true;
  }

  get_filtered_list(): void {
    this.isLoading = true;

    const params = {
      ...this.filterFields,
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type,
      },
    };

    localStorage.setItem('itemLists', JSON.stringify(params));
    this.pages_array = [];

    this.apiService.getCashInTransactions(params, this.current_page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.itemLists = response.result.data;

            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages,
            );
            this.total_pages  = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total        = parseInt(response.result.totalItems.toString(), 10);
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Cash In Transactions',
          );
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        },
      });
  }

  // ─── Maya Refresh (paused feature, logic unchanged) ───────────────────────

  openRefreshConfirmation(item: any): void {
    const state = this.getRefreshButtonState(item);
    if (!state || state.disabled) {
      return;
    }

    this.popUpDialog.openStatusActionDialog(
      'Do you want to refresh this transaction?',
      'Yes',
      'Cancel',
      () => this.executeRefresh(item),
    );
  }

  private executeRefresh(item: any): void {
    this.refreshingTransactionId = item.transactionId;

    this.spinner.show().catch((err) => {
      console.error('Spinner show error during refresh:', err);
    });

    this.apiService.refreshMayaTransaction({ transactionId: item.transactionId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.status === 200) {
            this.toastr.success(
              response?.message ?? 'Transaction refreshed successfully',
              'Refresh Successful',
            );
            // Refetch current page so the row's status (now likely non-Pending)
            // and the button state reflect reality — spinner is hidden by
            // get_filtered_list's own complete(), same as sortList()/setPage().
            if (this.filterFields) {
              this.get_filtered_list();
            } else {
              this.spinner.hide().catch((err) => {
                console.error('Spinner hide error after refresh:', err);
              });
            }
            return;
          }

          // response is an HttpErrorResponse-shaped object resolved via `next` —
          // the actual API body lives at response.error, not response.message
          const apiMessage = response?.error?.message;

          if (apiMessage === this.ALREADY_PROCESSED_MESSAGE) {
            this.processedTransactionIds.add(item.transactionId);
            this.toastr.error(this.ALREADY_PROCESSED_MESSAGE, 'Refresh Failed');
          } else {
            this.toastr.error('Something went wrong', 'Refresh Failed');
          }

          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error after refresh:', err);
          });
        },
        error: (error: any) => {
          // Rare: genuine transport failure with no response body at all
          this.toastr.error('Something went wrong', 'Refresh Failed');
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error after refresh:', err);
          });
        },
        complete: () => {
          this.refreshingTransactionId = null;
        },
      });
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setPaymentMethodType(text: string, value: string): void {
    this.paymentMethod = value;
    this.paymentMethodType_text = text;
  }

  setStatusType(text: string, value: string): void {
    this.status = value;
    this.statusType_text = text;
  }

  // ─── Table Controls Events ─────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo   = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate   = new Date(dateTo);

      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < fromDate) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type,
    );

    this.sort_column = column;
    this.sort_type   = type;

    if (this.submitted && this.filterFields) {
      this.get_filtered_list();
    }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) {
      this.get_filtered_list();
    }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) {
        this.get_filtered_list();
      }
    }
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    this.spinner.show().catch((error) => { console.log(error); });
    const current = new Date();
    this.filterFields = JSON.parse(localStorage.getItem('itemLists') || 'null');
    this.fileName = current.toLocaleDateString() + '-cash-in.xlsx';

    try {
      await this.request.postBlob(Urls.exportTransactionsCashin, this.filterFields).then((report: any) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = data;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((error) => { console.log(error); });
    } catch (error) {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
      this.spinner.hide().catch((err) => { console.log(err); });
    }
  }
}
